/**
 * rings — planetary rings, the PURE half (shader gaps R4, 2026-09-30 — the owner's report §6.5).
 *
 * No planet had rings. Every giant planet of our own system has them (NASA's ring fact sheets); Vorune is Atlas's gas
 * giant ("wide enough to swallow four Commons"), so it wears a DENSE, Saturn-like system, and a derived system's
 * gas-faced planet wears one by a hash. Everything the ring shader, the ring's shadow on the planet and the camera's
 * sun visibility decide by NUMBER lives here, three-free (`verify:space-light` pins it; `space_light_look.ts` is the
 * TSL twin — a shader that disagrees with its mirror is the bug):
 *
 *  - THE PROFILE: a radial optical-depth / albedo / dust-fraction table in planet radii, laid out from the NASA Saturn
 *    ring fact sheet (D 1.110–1.236, C 1.239–1.526, B 1.526–1.950, the Cassini Division to 2.030, A 2.030–2.270 with
 *    the Encke and Keeler gaps, the narrow F ring at 2.32; τ C 0.05–0.35, B 0.4–2.5, A 0.4–1.0) + a deterministic
 *    ringlet structure. Baked ONCE into a 2048×1 RGBA8 texture: R = the NORMAL-INCIDENCE transmittance T0 = e^−τ,
 *    G = the particles' single-scattering albedo ϖ0, B = the dust fraction. Its MIP CHAIN is a box filter of T0, so a
 *    lookup at the pixel's radial footprint is the AREA-AVERAGE transmittance (the anti-aliasing): the slant depth is
 *    then τ_eff / μ with τ_eff = −ln(T̄0) — exact at μ = 1, a small Jensen bias elsewhere, and never the shimmer a
 *    point sample gives when the Encke gap is a tenth of a pixel. `bakeRingProfile` + `sampleRingProfile` are the CPU
 *    mirror of the texture and of three's trilinear `.level()` lookup.
 *  - THE PHOTOMETRY: Chandrasekhar's single scattering in a plane-parallel slab, the formulas of Saturn ring
 *    photometry (Cuzzi et al.; Arnold & Schneider 2004):
 *      lit side (sun and eye on one side)   I/F = ϖ0 P(α)/4 · μ0/(μ0+μ) · (1 − e^{−τ(1/μ0 + 1/μ)})
 *      unlit side (the light came through)  I/F = ϖ0 P(α)/4 · μ0/(μ−μ0) · (e^{−τ/μ} − e^{−τ/μ0})   (→ ϖ0P/4·(τ/μ0)e^{−τ/μ0})
 *    with P(α) a mix of a macroscopic LAMBERT-SPHERE particle (backscattering: dense rings darken at high phase) and a
 *    forward-scattering Henyey–Greenstein dust lobe (g 0.7 — the D, F and Cassini-division dust GLOWS when you look
 *    back toward the sun through it). A dense layer of bright ice scatters more than once; `RING_MS_GAIN` stands for it
 *    (a documented tuning factor, not physics — Cuzzi's multiple-scattering term is ~1.5–2× at moderate phase).
 *  - EDGE-ON SAFETY: μ and μ0 are floored at `RING_MU_FLOOR` (the ring's finite thickness — never a divide by a near-zero
 *    cosine: no NaN, no infinite white line), and a sheet whose projected width falls under a pixel draws with that
 *    COVERAGE (`ringEdgeCoverage`) instead of as a sparse dashed line — at exact edge-on it vanishes, as Saturn's do at a
 *    ring-plane crossing.
 *  - THE SPLIT: the ring is drawn as TWO meshes — the half behind the planet's limb plane (renderOrder 1, before the
 *    cloud shell 1.5, the rim and the atmosphere shells 2) and the half in front (3): the atmosphere is additive and
 *    the ring is an "over", so the back half must be under the limb's light and the front half over it (`ringHalf`).
 *  - THE SHADOWS: the ring's shadow on anything (`ringShadowT`) is the profile at the point where the sun ray from the
 *    receiver crosses the ring plane, filtered by the SUN'S penumbra there (2·t·α_sun of ring radius — the soft band
 *    edge) and raised to 1/μ0. The planet's shadow ON the ring is the eclipse (eclipse.ts) with the planet as occluder.
 *
 * NO-CYCLE RULE: numbers in, numbers out.
 */

export type V3 = { x: number; y: number; z: number };

const clamp = (v: number, lo: number, hi: number): number => (v < lo ? lo : v > hi ? hi : v);
const smooth = (e0: number, e1: number, x: number): number => { const t = clamp((x - e0) / (e1 - e0), 0, 1); return t * t * (3 - 2 * t); };
const dot = (a: V3, b: V3): number => a.x * b.x + a.y * b.y + a.z * b.z;
const len = (a: V3): number => Math.hypot(a.x, a.y, a.z);
const norm = (a: V3): V3 => { const l = len(a) || 1; return { x: a.x / l, y: a.y / l, z: a.z / l }; };
const cross = (a: V3, b: V3): V3 => ({ x: a.y * b.z - a.z * b.y, y: a.z * b.x - a.x * b.z, z: a.x * b.y - a.y * b.x });

// ── the profile ──────────────────────────────────────────────────────────────────────────────────────────────────
/** The ring system's extent in planet radii (the D ring's inner edge → the F ring's dusty envelope). */
export const RING_R_IN = 1.110;
export const RING_R_OUT = 2.340;
/** The profile texture's width (texels across [R_IN, R_OUT]): 0.0006 R a texel — the Encke gap is ~10 texels. */
export const RING_TEX_W = 2048;
/** A band edge's softness (planet radii): 0.0012 R ≈ 290 m on Vorune — two texels. */
const EDGE = 0.0012;

interface Band { name: string; r0: number; r1: number; tau0: number; tau1: number; albedo: number; dust: number; ripple: number }
/** Laid out from the NASA Saturn ring fact sheet (radii in Saturn radii; τ and albedo ranges), for Vorune. */
export const RING_BANDS: ReadonlyArray<Band> = [
  { name: 'D', r0: 1.110, r1: 1.236, tau0: 0.002, tau1: 0.004, albedo: 0.30, dust: 0.95, ripple: 0.3 },
  { name: 'C', r0: 1.239, r1: 1.526, tau0: 0.07, tau1: 0.14, albedo: 0.24, dust: 0.12, ripple: 0.35 },
  { name: 'B', r0: 1.526, r1: 1.950, tau0: 1.0, tau1: 2.2, albedo: 0.62, dust: 0.0, ripple: 0.25 },
  { name: 'Cassini', r0: 1.950, r1: 2.030, tau0: 0.08, tau1: 0.11, albedo: 0.28, dust: 0.35, ripple: 0.4 },
  { name: 'A', r0: 2.030, r1: 2.270, tau0: 0.75, tau1: 0.45, albedo: 0.55, dust: 0.05, ripple: 0.15 },
  { name: 'F-envelope', r0: 2.300, r1: 2.340, tau0: 0.015, tau1: 0.015, albedo: 0.5, dust: 0.95, ripple: 0 },
  { name: 'F', r0: 2.316, r1: 2.324, tau0: 0.35, tau1: 0.35, albedo: 0.6, dust: 0.6, ripple: 0 },
];
/** Narrow ringlets (added optical depth) — the C ring's plateaus and the Titan / Maxwell / Huygens ringlets. */
const RINGLETS: ReadonlyArray<{ r: number; w: number; tau: number }> = [
  { r: 1.292, w: 0.004, tau: 0.45 }, { r: 1.353, w: 0.012, tau: 0.25 }, { r: 1.404, w: 0.010, tau: 0.3 },
  { r: 1.453, w: 0.0025, tau: 0.6 }, { r: 1.975, w: 0.003, tau: 0.35 }, { r: 2.010, w: 0.004, tau: 0.25 },
];
/** Gaps (a multiplier toward zero) — Maxwell, Huygens, Encke, Keeler. */
const GAPS: ReadonlyArray<{ r0: number; r1: number; depth: number }> = [
  { r0: 1.4495, r1: 1.4565, depth: 0.97 }, { r0: 1.9505, r1: 1.9600, depth: 0.95 },
  { r0: 2.2115, r1: 2.2165, depth: 0.985 }, { r0: 2.2640, r1: 2.2660, depth: 0.95 },
];
/** Deterministic ringlet structure: three incommensurate ripples (−1..1). */
function ripple(rho: number): number {
  return 0.5 * Math.sin(rho * 2 * Math.PI * 37.3) + 0.3 * Math.sin(rho * 2 * Math.PI * 91.7 + 1.3) + 0.2 * Math.sin(rho * 2 * Math.PI * 233.1 + 2.1);
}
const plateau = (rho: number, r0: number, r1: number): number => smooth(r0 - EDGE, r0 + EDGE, rho) * (1 - smooth(r1 - EDGE, r1 + EDGE, rho));

export interface RingPoint { tau: number; albedo: number; dust: number }
/** The profile at ρ planet radii (the analytic truth the texture samples). */
export function ringProfileAt(rho: number): RingPoint {
  let tau = 0, wA = 0, wD = 0, wSum = 0;
  for (const b of RING_BANDS) {
    const m = plateau(rho, b.r0, b.r1);
    if (m <= 0) continue;
    const f = clamp((rho - b.r0) / (b.r1 - b.r0), 0, 1);
    const t = (b.tau0 + (b.tau1 - b.tau0) * f) * (1 + b.ripple * ripple(rho)) * m;
    tau += t; wA += b.albedo * t; wD += b.dust * t; wSum += t;
  }
  for (const l of RINGLETS) { const m = plateau(rho, l.r - l.w / 2, l.r + l.w / 2); if (m > 0) { tau += l.tau * m; wA += 0.4 * l.tau * m; wD += 0.2 * l.tau * m; wSum += l.tau * m; } }
  for (const g of GAPS) tau *= 1 - g.depth * plateau(rho, g.r0, g.r1);
  return { tau: Math.max(0, tau), albedo: wSum > 0 ? wA / wSum : 0.3, dust: wSum > 0 ? wD / wSum : 0.9 };
}

export interface RingProfile { w: number; levels: Uint8Array[] }
/** Bake the RGBA8 profile + its box-filter mip chain (the CPU mirror of the GPU's generated mips). */
export function bakeRingProfile(w = RING_TEX_W): RingProfile {
  const l0 = new Uint8Array(w * 4);
  for (let i = 0; i < w; i++) {
    const rho = RING_R_IN + ((i + 0.5) / w) * (RING_R_OUT - RING_R_IN);
    const p = ringProfileAt(rho);
    l0[i * 4] = Math.round(Math.exp(-p.tau) * 255);
    l0[i * 4 + 1] = Math.round(clamp(p.albedo, 0, 1) * 255);
    l0[i * 4 + 2] = Math.round(clamp(p.dust, 0, 1) * 255);
    l0[i * 4 + 3] = 255;
  }
  const levels = [l0];
  for (let n = w; n > 1; n >>= 1) {
    const prev = levels[levels.length - 1], half = n >> 1, next = new Uint8Array(half * 4);
    for (let i = 0; i < half; i++) for (let c = 0; c < 4; c++) next[i * 4 + c] = Math.round((prev[i * 8 + c] + prev[i * 8 + 4 + c]) / 2);
    levels.push(next);
  }
  return { w, levels };
}

/** Linear sample of one level at u (0..1, clamp-to-edge, texel centres at (i+½)/w) → [T0, albedo, dust]. */
function sampleLevel(lv: Uint8Array, u: number): [number, number, number] {
  const n = lv.length / 4;
  const x = clamp(u, 0, 1) * n - 0.5;
  const i0 = clamp(Math.floor(x), 0, n - 1), i1 = clamp(i0 + 1, 0, n - 1), f = clamp(x - Math.floor(x), 0, 1);
  const g = (c: number): number => ((1 - f) * lv[i0 * 4 + c] + f * lv[i1 * 4 + c]) / 255;
  return [g(0), g(1), g(2)];
}
/** The ring LOD from a radial footprint (planet radii): log2 of the footprint in texels, ≥ 0 (the shader's mirror). */
export function ringLod(footprintRho: number, w = RING_TEX_W): number {
  return Math.max(0, Math.log2(Math.max(1e-6, footprintRho * (w / (RING_R_OUT - RING_R_IN)))));
}
/** Trilinear sample at ρ with a radial footprint (planet radii) — the mirror of `texture(profile, u).level(lod)`. */
export function sampleRingProfile(prof: RingProfile, rho: number, footprintRho = 0): { T0: number; albedo: number; dust: number } {
  const u = (rho - RING_R_IN) / (RING_R_OUT - RING_R_IN);
  if (u < 0 || u > 1) return { T0: 1, albedo: 0, dust: 0 };
  const lod = Math.min(prof.levels.length - 1, ringLod(footprintRho, prof.w));
  const l0 = Math.floor(lod), l1 = Math.min(prof.levels.length - 1, l0 + 1), f = lod - l0;
  const a = sampleLevel(prof.levels[l0], u), b = sampleLevel(prof.levels[l1], u);
  return { T0: a[0] + (b[0] - a[0]) * f, albedo: a[1] + (b[1] - a[1]) * f, dust: a[2] + (b[2] - a[2]) * f };
}
/** τ from a (filtered) normal-incidence transmittance, floored so an opaque texel stays finite. */
export function tauFromT0(T0: number): number { return -Math.log(Math.max(T0, 1e-4)); }

// ── photometry ───────────────────────────────────────────────────────────────────────────────────────────────────
/** The ring's finite thickness as a floor on the path cosines (a sheet ~1/250 of its width thick). */
export const RING_MU_FLOOR = 0.004;
/** The forward-scattering dust lobe (Henyey–Greenstein g). */
export const RING_G_DUST = 0.7;
/** Multiple scattering in a dense layer of bright ice (a documented tuning factor on the single-scatter I/F). */
export const RING_MS_GAIN = 1.6;
/** The particles' and the dust's colours (sRGB hex — the tan ice of Saturn's A/B rings, the blue-grey C ring dust). */
export const RING_PARTICLE_HEX = 0xd2c0a2;
export const RING_DUST_HEX = 0xaab6c8;

/** Henyey–Greenstein, normalised to a 4π mean of 1; `cosT` the scattering angle's cosine (1 = straight forward). */
export function hg(g: number, cosT: number): number {
  const g2 = g * g;
  return (1 - g2) / Math.pow(Math.max(1e-6, 1 + g2 - 2 * g * cosT), 1.5);
}
/** A Lambertian SPHERE's phase function (a macroscopic particle), normalised to a 4π mean of 1; α the phase angle. */
export function lambertSpherePhase(alpha: number): number {
  const a = clamp(alpha, 0, Math.PI);
  return (8 / (3 * Math.PI)) * (Math.sin(a) + (Math.PI - a) * Math.cos(a));
}
/** The ring's phase function at phase angle α (cosAlpha = L·V; 1 = opposition, the sun behind the eye). */
export function ringPhase(dust: number, cosAlpha: number): number {
  const d = clamp(dust, 0, 1);
  return (1 - d) * lambertSpherePhase(Math.acos(clamp(cosAlpha, -1, 1))) + d * hg(RING_G_DUST, -cosAlpha);
}
/**
 * The ring's reflectance I/F (multiply by the sun's radiance-convention colour, uPlanetSun, for radiance). `mu`, `mu0`
 * the |cosines| of the view and the sun to the ring normal; `litSide` true when the eye sees the side the sun lights.
 */
export function ringIF(tau: number, albedo: number, dust: number, mu: number, mu0: number, litSide: boolean, cosAlpha: number): number {
  const m = Math.max(mu, RING_MU_FLOOR), m0 = Math.max(mu0, RING_MU_FLOOR);
  const k = (albedo * ringPhase(dust, cosAlpha)) / 4;
  if (litSide) return RING_MS_GAIN * k * (m0 / (m0 + m)) * (1 - Math.exp(-tau * (1 / m0 + 1 / m)));
  if (Math.abs(m - m0) < 1e-3) return k * (tau / m0) * Math.exp(-tau / m0);
  return k * (m0 / (m - m0)) * (Math.exp(-tau / m) - Math.exp(-tau / m0));
}
/** The fraction of the background the ring hides along a view ray (1 − the direct transmittance). */
export function ringAlpha(tau: number, mu: number): number { return 1 - Math.exp(-tau / Math.max(mu, RING_MU_FLOOR)); }
/**
 * Near edge-on the sheet's projected width falls under a pixel: its fragments carry that COVERAGE (a thin, faint line
 * that vanishes at exact edge-on) rather than a sparse, dashed full-strength line. `widthM` the ring's radial width,
 * `pixelRad` one pixel's angle.
 */
export function ringEdgeCoverage(widthM: number, mu: number, distM: number, pixelRad: number): number {
  return clamp((widthM * Math.abs(mu)) / Math.max(1e-6, distM * pixelRad), 0, 1);
}
/**
 * Which half a ring point is drawn in: 'front' when it lies on the camera's side of the planet's LIMB plane
 * (P·C = R², the plane of the silhouette circle; `R` 0 = the plane through the centre, the pre-2026-10-07 cut). On a
 * view ray that meets the planet a front point is always before the visible cap and a back point always after it —
 * the ray enters through the cap (S·C > R²), leaves through the hidden part (X·C ≤ R²), and P·C only falls along it.
 */
export function ringHalf(pRel: V3, camRel: V3, R = 0): 'front' | 'back' { return dot(pRel, camRel) > R * R ? 'front' : 'back'; }
/** The ring halves' draw order against the planet's other transparent shells (THE SPLIT). */
export const RING_ORDER = { back: 1, cloud: 1.5, shells: 2, front: 3 } as const;

// ── geometry and shadows ─────────────────────────────────────────────────────────────────────────────────────────
export interface RingSpec {
  /** The ring's centre (the planet's centre) in the SAME frame as the receiver points. */
  centre: V3;
  /** The ring plane's unit normal. */
  normal: V3;
  /** The planet radius (m) and the ring's extent (planet radii). */
  planetR: number; rIn: number; rOut: number;
  /** Optical-depth scale (a derived system's rings run thinner or denser). */
  tauScale?: number;
}
/** Where the ray P + L·t (L unit, toward the sun) crosses the ring plane: ρ (planet radii), t (m), μ0 = |L·n|. */
export function ringHit(P: V3, L: V3, ring: RingSpec): { rho: number; t: number; mu0: number } | null {
  const n = ring.normal;
  const ln = dot(L, n);
  if (Math.abs(ln) < 1e-6) return null;
  const t = dot({ x: ring.centre.x - P.x, y: ring.centre.y - P.y, z: ring.centre.z - P.z }, n) / ln;
  if (t <= 0) return null;
  const hx = P.x + L.x * t - ring.centre.x, hy = P.y + L.y * t - ring.centre.y, hz = P.z + L.z * t - ring.centre.z;
  return { rho: Math.hypot(hx, hy, hz) / ring.planetR, t, mu0: Math.abs(ln) };
}
/**
 * The ring's shadow at a receiver P: the profile where the sun ray crosses the ring, box-filtered over the SUN'S
 * penumbra there (2·t·α_sun, in ring radius — the soft band edge) plus any extra footprint (a pixel's), raised to the
 * slant 1/μ0. 1 = no ring in the way.
 */
export function ringShadowT(P: V3, L: V3, sunAngR: number, ring: RingSpec, prof: RingProfile, extraFootprintRho = 0): number {
  const h = ringHit(P, L, ring);
  if (!h || h.rho < ring.rIn || h.rho > ring.rOut) return 1;
  const pen = (2 * h.t * sunAngR) / ring.planetR;
  const foot = Math.hypot(pen, extraFootprintRho);
  const rhoStd = RING_R_IN + ((h.rho - ring.rIn) / (ring.rOut - ring.rIn)) * (RING_R_OUT - RING_R_IN); // a derived ring's radii map onto the profile
  const s = sampleRingProfile(prof, rhoStd, foot * ((RING_R_OUT - RING_R_IN) / (ring.rOut - ring.rIn)));
  const tau = tauFromT0(s.T0) * (ring.tauScale ?? 1);
  return Math.exp(-tau / Math.max(h.mu0, RING_MU_FLOOR));
}

// ── which rings exist ────────────────────────────────────────────────────────────────────────────────────────────
/**
 * A ring normal with the sun `sunElevDeg` above the ring plane and a viewer along `viewDir` (from the viewer TO the
 * planet) seeing the LIT face at `openDeg`: n·L = sin(sunElev), n·(−viewDir) = sin(open) (same sign = the lit side).
 */
export function ringNormalFor(sunDir: V3, viewDir: V3, sunElevDeg: number, openDeg: number): V3 {
  const L = norm(sunDir), d = norm(viewDir);
  const a = Math.sin((sunElevDeg * Math.PI) / 180), b = -Math.sin((openDeg * Math.PI) / 180); // n·d = −sin(open)
  // n = αL + βd + γ(L×d): solve the 2×2 for α, β, then γ from |n| = 1.
  const c = dot(L, d);
  const det = 1 - c * c;
  const alpha = (a - b * c) / det, beta = (b - a * c) / det;
  const inPlane = { x: alpha * L.x + beta * d.x, y: alpha * L.y + beta * d.y, z: alpha * L.z + beta * d.z };
  const rest = Math.max(0, 1 - dot(inPlane, inPlane));
  const x = norm(cross(L, d));
  return norm({ x: inPlane.x + x.x * Math.sqrt(rest), y: inPlane.y + x.y * Math.sqrt(rest), z: inPlane.z + x.z * Math.sqrt(rest) });
}
/**
 * Vorune's ring (sys0): the sun 22° above the ring plane (the planet's shadow falls across the ring behind it and the
 * ring's shadow is a band on the planet — Saturn's own B′ ranges ±27°), the home cluster (the launch ring at the
 * origin) seeing the LIT face 20° open. The sector sun is `space_look.SYS0_SUN_DIR`; Vorune sits at (−725, 260, 690) km.
 */
export const VORUNE_POS: V3 = { x: -725_000, y: 260_000, z: 690_000 };
export const VORUNE_R = 240_000;
const SYS0_SUN: V3 = norm({ x: 0.55, y: 0.8, z: 0.3 });
export const VORUNE_RING_NORMAL: V3 = ringNormalFor(SYS0_SUN, norm(VORUNE_POS), 22, 20);
/** The ring spec of a sys0 planet by name (only Vorune). */
export function sys0RingFor(name: string): { rIn: number; rOut: number; normal: V3; tauScale: number } | null {
  return name === 'Vorune' ? { rIn: RING_R_IN, rOut: RING_R_OUT, normal: VORUNE_RING_NORMAL, tauScale: 1 } : null;
}
const fnv = (s: string): number => { let h = 0x811c9dc5; for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 0x01000193); } h ^= h >>> 16; h = Math.imul(h, 0x85ebca6b); h ^= h >>> 13; return (h >>> 0) / 4294967296; };
/** The derived systems' pooled face index of the gas giant (`space_sector.WORLD_PLANET_POOL[1]`). */
export const GAS_FACE_IDX = 1;
/**
 * A derived system's planet rings (a hash): only a GAS-faced planet, 60 % of them; the extent 1.2–1.4 → 1.9–2.4 radii;
 * the optical depth ×0.4–1.2; the normal tipped so the star at the ORIGIN sits 12–32° above the plane (never edge-on).
 */
export function derivedRingFor(systemId: string, i: number, texIdx: number, pos: V3): { rIn: number; rOut: number; normal: V3; tauScale: number } | null {
  if (texIdx !== GAS_FACE_IDX) return null;
  const h = (k: string): number => fnv(`${systemId}:ring:${i}:${k}`);
  if (h('has') >= 0.6) return null;
  const s = norm({ x: -pos.x, y: -pos.y, z: -pos.z }); // the star from the planet
  const elev = ((12 + h('elev') * 20) * Math.PI) / 180, az = h('az') * 2 * Math.PI;
  const u = norm(cross(s, Math.abs(s.y) < 0.9 ? { x: 0, y: 1, z: 0 } : { x: 1, y: 0, z: 0 })), v = cross(s, u);
  const inPlane = { x: Math.cos(az) * u.x + Math.sin(az) * v.x, y: Math.cos(az) * u.y + Math.sin(az) * v.y, z: Math.cos(az) * u.z + Math.sin(az) * v.z };
  const n = norm({ x: s.x * Math.sin(elev) + inPlane.x * Math.cos(elev), y: s.y * Math.sin(elev) + inPlane.y * Math.cos(elev), z: s.z * Math.sin(elev) + inPlane.z * Math.cos(elev) });
  return { rIn: 1.2 + h('in') * 0.2, rOut: 1.9 + h('out') * 0.5, normal: n, tauScale: 0.4 + h('tau') * 0.8 };
}
